// TODO: to be up lifted and reused in AddLocationTagPage component

import { Box, Button, Checkbox, Grid, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Pagination } from 'src/components/pagination/Pagination';
import { Progress } from 'src/components/progress';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { FIXED_FOOTER_HEIGHT, MAX_LOCATION_TAG } from 'src/constants';
import { ILocation } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import {
  CheckboxLabel,
  GridContainer,
  LocationContainer,
  LocationSectionHeader,
  StyledGridHeader,
} from './LocationList.styled';

interface ILocationListPropTypes {
  locations: ILocation[];
  isLoadingLocations: boolean;
  tagId: number;
  totalPages: number;
  totalCount: number;
  page: number;
  handlePageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  handleUpdateLocations: (
    locations: ILocation[],
    isDeselectAll?: boolean,
  ) => void;
  locationsSelectedCount: number;
  setLocationsSelectedCount: React.Dispatch<React.SetStateAction<number>>;
  setDelta?: React.Dispatch<React.SetStateAction<Map<number | string, number>>>;
}

export const LocationList = ({
  locations,
  isLoadingLocations,
  totalPages,
  totalCount,
  page,
  handlePageChange,
  tagId,
  handleUpdateLocations,
  locationsSelectedCount,
  setLocationsSelectedCount,
  setDelta,
}: ILocationListPropTypes) => {
  const { isViewOnly } = useUserRole();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const itemsPerPage = 50;
  const start = (page - 1) * itemsPerPage + 1;
  const end = Math.min(page * itemsPerPage, totalCount);

  const selectableLocations = locations.filter(
    (loc) => loc.location_tag_id?.length < MAX_LOCATION_TAG,
  );

  const locationIsChecked = (location: ILocation, id: number): boolean => {
    return location.location_tag_id?.includes(id);
  };

  const allSelectableAreChecked = selectableLocations.every((loc) =>
    locationIsChecked(loc, tagId),
  );

  const selectLocation = (location: ILocation, id: number): ILocation => {
    if (!location.location_tag_id?.includes(id)) {
      setLocationsSelectedCount((prev) => prev + 1);

      if (setDelta) {
        setDelta((prev) => {
          const next = new Map(prev);
          next.set(location.location_code, 1);

          return next;
        });
      }

      return {
        ...location,
        location_tag_id: [...location.location_tag_id, id],
      };
    }

    return location;
  };

  const deselectLocation = (location: ILocation, id: number): ILocation => {
    const deselected = {
      ...location,
      location_tag_id: location.location_tag_id.filter((tagId) => tagId !== id),
    };

    setLocationsSelectedCount((prev) => prev - 1);

    if (setDelta) {
      setDelta((prev) => {
        const next = new Map(prev);
        next.set(location.location_code, -1);

        return next;
      });
    }

    return deselected;
  };

  const toggleLocationSelection = (
    location: ILocation,
    id: number,
  ): ILocation => {
    if (locationIsChecked(location, id)) {
      return deselectLocation(location, id);
    }

    return selectLocation(location, id);
  };

  const handleSelectAllClick = () => {
    if (!allSelectableAreChecked) {
      handleUpdateLocations(
        locations.map((location) =>
          location.location_tag_id?.length < MAX_LOCATION_TAG
            ? selectLocation(location, tagId)
            : location,
        ),
      );
    } else {
      setLocationsSelectedCount(0);
      handleUpdateLocations(
        locations.map((location) =>
          locationIsChecked(location, tagId)
            ? {
                ...location,
                location_tag_id: location.location_tag_id.filter(
                  (id) => id !== tagId,
                ),
              }
            : location,
        ),
        true,
      );
    }
  };

  const handleLocationCheckboxChange = (selectedLocation: ILocation) => {
    if (
      selectedLocation.location_tag_id?.length >= MAX_LOCATION_TAG &&
      !locationIsChecked(selectedLocation, tagId)
    ) {
      return;
    }

    const updated = locations.map((location) => {
      if (selectedLocation === location) {
        return toggleLocationSelection(location, tagId);
      }

      return location;
    });

    handleUpdateLocations(updated);
  };

  useEffect(() => {
    if (!isLoadingLocations && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [page, isLoadingLocations]);

  return (
    <LocationContainer>
      <LocationSectionHeader>
        {locations.length > 0 && (
          <>
            <Typography variant="body1" color="grey" flexGrow={1}>
              {`Showing ${start}–${end} of ${totalCount}`}
            </Typography>
            <Typography variant="body1" color="grey" marginRight={1}>
              {`${locationsSelectedCount} Selected`}
            </Typography>
            <Button
              variant="text"
              onClick={handleSelectAllClick}
              disabled={selectableLocations.length === 0}>
              {allSelectableAreChecked ? lang.deselectAll : lang.selectAll}
            </Button>
          </>
        )}
        {locations.length === 0 && (
          <Typography variant="body1" color="grey" marginBottom={1}>
            {`Showing 0/0`}
          </Typography>
        )}
      </LocationSectionHeader>
      <GridContainer ref={scrollContainerRef}>
        <StyledGridHeader>
          <Typography variant="h6" component="h2" gutterBottom>
            {lang.selectLocations}
          </Typography>
        </StyledGridHeader>
        {isLoadingLocations ? (
          <Progress />
        ) : locations.length === 0 ? (
          <NoResultAlert />
        ) : (
          <>
            <Grid container spacing={1} flex={1}>
              {locations.map((location: ILocation) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={4}
                  xl={4}
                  key={location.location_code}>
                  <CustomTooltip
                    width={100}
                    placement="left"
                    title={lang.maxTags}
                    isHidden={
                      location.location_tag_id?.length < MAX_LOCATION_TAG ||
                      locationIsChecked(location, tagId)
                    }
                    skipValidateOverflow={true}>
                    <CheckboxLabel
                      label={`${location?.store_number ?? ''} - ${
                        location?.name ?? ''
                      }`}
                      control={
                        <Checkbox
                          disabled={
                            location.location_tag_id?.length >=
                              MAX_LOCATION_TAG &&
                            !locationIsChecked(location, tagId)
                          }
                          checked={locationIsChecked(location, tagId)}
                          onChange={() =>
                            handleLocationCheckboxChange(location)
                          }
                        />
                      }
                    />
                  </CustomTooltip>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </GridContainer>
      <Box
        display="flex"
        justifyContent="center"
        mt={2}
        pb={!isViewOnly ? `${FIXED_FOOTER_HEIGHT}px` : 0}>
        <Pagination
          totalPages={totalPages}
          page={page}
          onChange={handlePageChange}
        />
      </Box>
    </LocationContainer>
  );
};
